import { useEffect, useState } from "react";
import { CheckCircle, SignIn, SignOut, ArrowLeft, Backspace, Island, PaperPlaneRight, CurrencyCircleDollar, Camera, X, Storefront, Timer } from "@phosphor-icons/react";
import { format, addDays } from "date-fns";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import KioskPos from "./KioskPos";
import KioskPasse from "./KioskPasse";
import { OrbMark, InitialAvatar } from "../components/Brand";

function ClockedInList({ venueId }) {
  const [list, setList] = useState([]);
  useEffect(() => {
    const load = () => api.get("/kiosk/status", { params: venueId ? { venue_id: venueId } : {} }).then((r) => setList(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [venueId]);
  if (list.length === 0) return null;
  return (
    <div className="glass rounded-2xl p-4" data-testid="kiosk-clocked-in-list">
      <p className="text-[#79728C] text-xs uppercase tracking-widest mb-2">Currently clocked in</p>
      <div className="space-y-1.5">
        {list.map((e) => {
          const mins = Math.max(0, Math.floor((Date.now() - new Date(e.clock_in).getTime()) / 60000));
          const txt = `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`;
          return (
            <div key={e.employee_id} className="flex justify-between text-sm" data-testid={`kiosk-clocked-${e.employee_id}`}>
              <span>{e.name}</span>
              <span className="text-[#8FA98C] font-medium">{txt}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Kiosk() {  const [staff, setStaff] = useState([]);
  const [step, setStep] = useState("pick"); // pick | pin | menu | result | leave | request
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState("");
  const [session, setSession] = useState(null); // {token, employee}
  const [result, setResult] = useState(null);
  const [leaveData, setLeaveData] = useState(null);
  const [req, setReq] = useState({ type: "annual", start_date: "", end_date: "", reason: "" });
  const [revForm, setRevForm] = useState(null);
  const [invFiles, setInvFiles] = useState([]);
  const [invMode, setInvMode] = useState("single");
  const [invJobs, setInvJobs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [venues, setVenues] = useState(null);
  const [venue, setVenue] = useState(() => localStorage.getItem("mise_kiosk_venue") || "");

  useEffect(() => {
    api.get("/kiosk/venues").then((r) => {
      setVenues(r.data);
      if (!venue && r.data.length === 1) pickVenue(r.data[0].id);
    }).catch(() => setVenues([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickVenue = (id) => {
    localStorage.setItem("mise_kiosk_venue", id);
    setVenue(id);
  };

  useEffect(() => {
    if (!venue) return;
    api.get("/kiosk/staff", { params: { venue_id: venue } }).then((r) => setStaff(r.data)).catch(() => toast.error("Could not load staff list"));
  }, [venue]);

  // Auto-reset to staff picker after inactivity — activity-aware (any tap/keypress re-arms)
  // and visibility-aware (paused while the tab is in the background, so switching away
  // and coming back does NOT sign you out; the 90s countdown resumes on return).
  useEffect(() => {
    if (step === "pick") return;
    let timer;
    const arm = () => {
      clearTimeout(timer);
      if (document.hidden) return;
      timer = setTimeout(reset, 90000);
    };
    const onVis = () => { if (document.hidden) { clearTimeout(timer); } else { arm(); } };
    arm();
    window.addEventListener("pointerdown", arm);
    window.addEventListener("keydown", arm);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("pointerdown", arm);
      window.removeEventListener("keydown", arm);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [step, result]);

  // Sliding session renewal: kiosk tokens last 12h; refresh every 5 min while signed in.
  useEffect(() => {
    if (!session) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.post("/kiosk/token/refresh", {}, { headers: { Authorization: `Bearer ${session.token}` } });
        setSession((s) => (s ? { ...s, token: data.token } : s));
      } catch { /* a one-shot refresh+retry also happens on any failing action */ }
    }, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [session?.token]);

  const reset = () => {
    setStep("pick"); setSelected(null); setPin(""); setSession(null);
    setResult(null); setLeaveData(null); setRevForm(null);
    setInvFiles([]); setInvMode("single"); setInvJobs(null);
    setReq({ type: "annual", start_date: "", end_date: "", reason: "" });
  };

  useEffect(() => {
    if (pin.length === 4 && selected && !busy) doAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  useEffect(() => {
    if (step === "result") {
      const t = setTimeout(() => setStep("menu"), 4000);
      return () => clearTimeout(t);
    }
  }, [step]);

  const doAuth = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/kiosk/auth", { employee_id: selected.id, pin });
      setSession(data);
      setStep("menu");
    } catch (e) {
      toast.error(apiError(e, "Invalid PIN"));
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  const kioskApi = async (method, url, body) => {
    try {
      return await api({ method, url, data: body, headers: { Authorization: `Bearer ${session.token}` } });
    } catch (e) {
      if (e.response?.status !== 401) throw e;
      // token expired (e.g. tab left open past the token lifetime) — refresh once and retry
      try {
        const { data } = await api.post("/kiosk/token/refresh", {}, { headers: { Authorization: `Bearer ${session.token}` } });
        setSession((s) => (s ? { ...s, token: data.token } : s));
        return await api({ method, url, data: body, headers: { Authorization: `Bearer ${data.token}` } });
      } catch (e2) {
        toast.error("Session expired — please sign in again");
        reset();
        throw e2;
      }
    }
  };

  const doClock = async (action) => {
    setBusy(true);
    try {
      const { data } = await kioskApi("post", "/kiosk/clock", { action });
      setResult(data);
      setStep("result");
    } catch (e) {
      toast.error(apiError(e, "Clock failed"));
    } finally {
      setBusy(false);
    }
  };

  const loadLeave = async () => {
    setBusy(true);
    try {
      const { data } = await kioskApi("get", "/kiosk/leave");
      setLeaveData(data);
      setStep("leave");
    } catch (e) {
      toast.error(apiError(e, "Could not load leave"));
    } finally {
      setBusy(false);
    }
  };

  const submitLeave = async () => {
    if (!req.start_date || !req.end_date) return toast.error("Pick start and end dates");
    setBusy(true);
    try {
      await kioskApi("post", "/kiosk/leave", req);
      toast.success("Leave request submitted — managers notified");
      setStep("menu");
      setReq({ type: "annual", start_date: "", end_date: "", reason: "" });
    } catch (e) {
      toast.error(apiError(e, "Submit failed"));
    } finally {
      setBusy(false);
    }
  };

  const submitInvoice = async () => {
    if (!invFiles.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      invFiles.forEach((f) => fd.append("files", f));
      fd.append("mode", invMode);
      const { data } = await api.post("/kiosk/invoices/ocr", fd, { headers: { Authorization: `Bearer ${session.token}` } });
      const tracker = invMode === "single"
        ? [{ name: `${invFiles.length} file(s) — one invoice`, jobId: data.job_ids[0], status: "processing" }]
        : invFiles.map((f, i) => ({ name: f.name, jobId: data.job_ids[i], status: "processing" }));
      setInvJobs([...tracker]);
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        let pending = false;
        for (const t of tracker) {
          if (t.status === "processing") {
            try { Object.assign(t, (await kioskApi("get", `/kiosk/invoices/ocr/${t.jobId}`)).data); } catch (err) { console.warn("OCR poll failed, will retry:", err?.message); }
          }
          if (t.status === "processing") pending = true;
        }
        setInvJobs([...tracker]);
        if (!pending) break;
      }
      tracker.forEach((t) => { if (t.status === "processing") { t.status = "failed"; t.error = "Timed out"; } });
      setInvJobs([...tracker]);
    } catch (e) {
      toast.error(apiError(e, "Upload failed"));
    } finally {
      setBusy(false);
    }
  };

  const openRevenue = async () => {
    setBusy(true);
    const today = format(new Date(), "yyyy-MM-dd");
    try {
      const { data } = await kioskApi("get", `/kiosk/revenue?day=${today}`);
      setRevForm({ date: today, ...(data.entry || {}) });
      setStep("revenue");
    } catch (e) {
      toast.error(apiError(e, "No permission to capture revenue"));
    } finally {
      setBusy(false);
    }
  };

  const loadRevenueDate = async (day) => {
    setRevForm((f) => ({ ...f, date: day }));
    try {
      const { data } = await kioskApi("get", `/kiosk/revenue?day=${day}`);
      setRevForm({ date: day, ...(data.entry || { food_sales: 0, beverage_sales: 0, revenue_additional: 0, tips_pos_cash: 0, tips_pos_card: 0, tips_add_cash: 0, tips_add_card: 0 }) });
    } catch (err) {
      console.warn("Could not load revenue entry for", day, err?.message);
    }
  };

  const submitRevenue = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(revForm).map(([k, v]) =>
        [k, k === "date" ? v : k.startsWith("covers") ? (v === "" || v == null ? null : Number(v)) : Number(v) || 0]));
      await kioskApi("put", "/kiosk/revenue", payload);
      toast.success("Revenue & tips saved — distribution recalculated");
      setStep("menu");
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally {
      setBusy(false);
    }
  };

  const press = (k) => {
    if (k === "back") return setPin((p) => p.slice(0, -1));
    if (k === "clear") return setPin("");
    if (pin.length < 4) setPin((p) => p + k);
  };

  const tile = "w-full glass glass-hover rounded-2xl p-6 min-h-[96px] text-left active:scale-95 transition-colors duration-150 flex items-center gap-4";

  return (
    <div className="min-h-screen text-white px-6 pb-6 md:px-10 md:pb-10 pt-safe-lg" style={{ background: "radial-gradient(70rem 46rem at 20% -10%, rgba(74,29,150,0.35), transparent 60%), radial-gradient(50rem 36rem at 110% 110%, rgba(157,92,255,0.12), transparent 60%), #07050A" }}>
      <div className="max-w-2xl mx-auto space-y-8">
        <div className="text-center">
          <div className="flex flex-col items-center gap-4">
            <OrbMark size={110} animated />
            <p className="brand-word text-white text-3xl" data-testid="kiosk-wordmark">MISE <span className="text-[#9D5CFF]">SHIFT</span></p>
            <p className="text-[#8E87A3] text-[11px] uppercase tracking-[0.3em]" data-testid="kiosk-tagline">Prepared Intelligence</p>
          </div>
          <p className="text-[#BDB8CC] mt-2" data-testid="kiosk-subtitle">
            {step === "pick" && "Tap your name to start"}
            {step === "pin" && `Enter PIN for ${selected?.first_name}`}
            {step !== "pick" && step !== "pin" && session && `${session.employee.first_name} ${session.employee.last_name}`}
          </p>
        </div>

        {step === "pick" && !venue && venues && (
          <div data-testid="kiosk-venue-picker" className="space-y-4">
            <p className="text-white/60 text-center">Which venue is this device at? (remembered on this tablet)</p>
            <div className="grid grid-cols-1 gap-4">
              {venues.map((v) => (
                <button key={v.id} data-testid={`kiosk-venue-${v.id}`} onClick={() => pickVenue(v.id)} className={tile.replace("flex items-center gap-4", "")}>
                  <p className="font-heading font-semibold text-xl">{v.name}</p>
                </button>
              ))}
            </div>
          </div>
        )}
        {step === "pick" && venue && (
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-10 py-6" data-testid="kiosk-staff-grid">
            {staff.map((s) => (
              <button key={s.id} data-testid={`kiosk-staff-${s.id}`} onClick={() => { setSelected(s); setPin(""); setStep("pin"); }}
                className="flex flex-col items-center gap-3 group active:scale-95 transition-transform duration-150">
                <div className="group-hover:shadow-[0_0_24px_rgba(157,92,255,0.35)] rounded-full transition-shadow duration-200 group-hover:[&>div]:border-[#9D5CFF]/70">
                  <InitialAvatar firstName={s.first_name} lastName={s.last_name} size={96} />
                </div>
                <div className="text-center">
                  <p className="font-heading font-semibold text-base leading-tight">{s.first_name}</p>
                  <p className="font-heading font-semibold text-base leading-tight">{s.last_name}</p>
                  <p className="text-[#79728C] text-xs mt-1">{s.position}</p>
                </div>
              </button>
            ))}
            {staff.length === 0 && <p className="text-[#79728C] text-center py-12 w-full">Loading staff…</p>}
          </div>
        )}
        {step === "pick" && venue && venues && venues.length > 1 && (
          <button data-testid="kiosk-switch-venue" onClick={() => pickVenue("")}
            className="w-full text-center text-[#79728C] hover:text-white/80 transition-colors py-1 text-xs">
            Wrong venue? Switch
          </button>
        )}
        {step === "pick" && venue && <ClockedInList venueId={venue} />}

        {step === "pin" && (
          <div className="space-y-6" data-testid="kiosk-pin-pad">
            <button onClick={() => { setStep("pick"); setSelected(null); setPin(""); }} data-testid="kiosk-back"
              className="flex items-center gap-2 text-[#BDB8CC] hover:text-white transition-colors">
              <ArrowLeft size={20} /> Back
            </button>
            <div className="flex justify-center gap-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={`w-5 h-5 rounded-full border-2 transition-colors ${pin.length > i ? "bg-[#9D5CFF] border-[#9D5CFF] shadow-[0_0_10px_rgba(157,92,255,0.6)]" : "border-white/25"}`} />
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
              {["1","2","3","4","5","6","7","8","9","clear","0","back"].map((k) => (
                <button key={k} data-testid={`kiosk-key-${k}`} onClick={() => press(k)}
                  className="h-20 md:h-24 rounded-2xl glass text-2xl font-light text-white hover:bg-[#231D40]/80 active:scale-95 transition-colors duration-75 flex items-center justify-center">
                  {k === "back" ? <Backspace size={26} /> : k === "clear" ? <span className="text-base">C</span> : k}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === "pos" && session && (
          <div className="fixed inset-0 z-50" data-testid="kiosk-pos-overlay">
            <KioskPos session={session} kioskApi={kioskApi} onExit={() => setStep("menu")} />
          </div>
        )}

        {step === "passe" && session && (
          <KioskPasse session={session} kioskApi={kioskApi} onExit={() => setStep("menu")} />
        )}

        {step === "menu" && session && (
          <div className="space-y-4" data-testid="kiosk-menu">
            <div className="grid grid-cols-2 gap-4">
              <button onClick={() => doClock("in")} disabled={busy} data-testid="kiosk-clock-in-btn"
                className="rounded-2xl p-6 min-h-[120px] active:scale-95 transition-all duration-150 flex items-center gap-4 text-left text-white hover:brightness-110"
                style={{ background: "linear-gradient(135deg, #FF5C00, #CC4A00)", boxShadow: "0 8px 32px rgba(255,92,0,0.3)" }}>
                <SignIn size={40} weight="bold" />
                <div><p className="font-heading font-bold text-2xl">Clock in</p><p className="text-white/80 text-sm">Start your shift</p></div>
              </button>
              <button onClick={() => doClock("out")} disabled={busy} data-testid="kiosk-clock-out-btn"
                className="glass glass-hover rounded-2xl p-6 min-h-[120px] active:scale-95 transition-colors duration-150 flex items-center gap-4 text-left">
                <SignOut size={40} weight="bold" className="text-[#D4B3FF]" />
                <div><p className="font-heading font-semibold text-2xl">Clock out</p><p className="text-[#79728C] text-sm">End your shift</p></div>
              </button>
            </div>
            <button onClick={loadLeave} disabled={busy} data-testid="kiosk-leave-balance-btn" className={tile}>
              <Island size={36} weight="bold" className="text-[#D4B3FF]" />
              <div><p className="font-heading font-semibold text-xl">Leave balance</p><p className="text-[#79728C] text-sm">Accrued, taken and recent requests</p></div>
            </button>
            <button onClick={() => setStep("request")} data-testid="kiosk-request-leave-btn" className={tile}>
              <PaperPlaneRight size={36} weight="bold" className="text-[#D4B3FF]" />
              <div><p className="font-heading font-semibold text-xl">Request leave</p><p className="text-[#79728C] text-sm">Sick, annual or family responsibility</p></div>
            </button>
            {session.can_capture_revenue && (
              <button onClick={openRevenue} disabled={busy} data-testid="kiosk-revenue-btn" className={tile}>
                <CurrencyCircleDollar size={36} weight="bold" className="text-[#D4B3FF]" />
                <div><p className="font-heading font-semibold text-xl">Revenue & tips</p><p className="text-[#79728C] text-sm">Capture today's takings</p></div>
              </button>
            )}
            {(session.can_capture_invoices ?? session.can_capture_revenue) && (
              <button onClick={() => { setInvFiles([]); setInvJobs(null); setInvMode("single"); setStep("invoice"); }} data-testid="kiosk-invoice-btn" className={tile}>
                <Camera size={36} weight="bold" className="text-[#FF5C00]" />
                <div><p className="font-heading font-semibold text-xl">Invoice capture</p><p className="text-[#79728C] text-sm">Photograph a supplier invoice</p></div>
              </button>
            )}
            {session.can_passe && (
              <button onClick={() => setStep("passe")} data-testid="kiosk-passe-btn" className={tile}>
                <Timer size={36} weight="bold" className="text-[#D4B3FF]" />
                <div><p className="font-heading font-semibold text-xl">Passé board</p><p className="text-[#79728C] text-sm">Course pacing for tasting menus</p></div>
              </button>
            )}
            {/* POS tile hidden while the POS ordering system is on hold — kiosk clock/leave/revenue/invoice stay live */}
            {false && session.can_pos && (
              <button onClick={() => setStep("pos")} data-testid="kiosk-pos-btn" className={tile}>
                <Storefront size={36} weight="bold" className="text-[#D4B3FF]" />
                <div><p className="font-heading font-semibold text-xl">POS — Tables</p><p className="text-[#79728C] text-sm">Open tables, take orders, close receipts</p></div>
              </button>
            )}
            <button onClick={reset} data-testid="kiosk-done-btn"
              className="w-full text-center text-[#79728C] hover:text-white transition-colors py-3 text-sm">
              Done — back to staff list
            </button>
            <ClockedInList venueId={venue} />
          </div>
        )}

        {step === "result" && result && (
          <div className="text-center space-y-6 py-10" data-testid="kiosk-result">
            <CheckCircle size={96} weight="fill" className="mx-auto text-[#9D5CFF] drop-shadow-[0_0_24px_rgba(157,92,255,0.5)]" />
            <h1 className="font-heading text-4xl font-bold">{result.name}</h1>
            <div className="flex items-center justify-center gap-3 text-2xl">
              {result.action === "in" ? <SignIn size={32} weight="bold" /> : <SignOut size={32} weight="bold" />}
              <span>{result.action === "in" ? "Clocked in" : "Clocked out"} at {result.time}</span>
            </div>
            {result.action === "out" && <p className="text-white/70 text-lg" data-testid="kiosk-hours">Shift: {result.hours} hours</p>}
          </div>
        )}

        {step === "leave" && leaveData && (
          <div className="space-y-6" data-testid="kiosk-leave-view">
            <button onClick={() => setStep("menu")} data-testid="kiosk-leave-back" className="flex items-center gap-2 text-[#BDB8CC] hover:text-white transition-colors">
              <ArrowLeft size={20} /> Back
            </button>
            <div className="grid grid-cols-3 gap-4 text-center">
              {[["Accrued", leaveData.balance.accrued, "kiosk-accrued"], ["Taken", leaveData.balance.taken, "kiosk-taken"], ["Balance", leaveData.balance.balance, "kiosk-balance"]].map(([label, val, tid]) => (
                <div key={tid} className="glass rounded-2xl p-5">
                  <p className="text-white/50 text-xs">{label}</p>
                  <p className="font-heading text-3xl font-bold mt-1" data-testid={tid}>{val}</p>
                  <p className="text-white/40 text-[10px]">days</p>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <p className="text-[#79728C] text-sm">Recent requests</p>
              {leaveData.requests.length === 0 && <p className="text-white/40 text-sm">No requests yet.</p>}
              {leaveData.requests.map((r) => (
                <div key={r.id} className="flex justify-between glass rounded-xl px-4 py-3 text-sm" data-testid={`kiosk-leave-req-${r.id}`}>
                  <span className="capitalize">{r.type} · {r.start_date} → {r.end_date}</span>
                  <span className={`capitalize font-medium ${r.status === "approved" ? "text-[#9D5CFF]" : r.status === "declined" ? "text-[#79728C]" : "text-[#FF5C00]"}`}>{r.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === "request" && (
          <div className="space-y-5" data-testid="kiosk-leave-form">
            <button onClick={() => setStep("menu")} data-testid="kiosk-request-back" className="flex items-center gap-2 text-[#BDB8CC] hover:text-white transition-colors">
              <ArrowLeft size={20} /> Back
            </button>
            <div className="grid grid-cols-3 gap-3">
              {[["annual", "Annual"], ["sick", "Sick"], ["family", "Family"]].map(([v, label]) => (
                <button key={v} data-testid={`kiosk-leave-type-${v}`} onClick={() => setReq({ ...req, type: v })}
                  className={`h-16 rounded-xl border font-heading font-semibold transition-colors ${req.type === v ? "bg-[#9D5CFF] border-[#9D5CFF] text-white shadow-[0_0_20px_rgba(157,92,255,0.35)]" : "glass text-[#BDB8CC] hover:bg-[#231D40]/80"}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-white/50 text-xs mb-1.5">From</p>
                <input type="date" data-testid="kiosk-leave-start" value={req.start_date} min={format(new Date(), "yyyy-MM-dd")}
                  onChange={(e) => setReq({ ...req, start_date: e.target.value })}
                  className="w-full h-14 rounded-xl glass px-4 text-white [color-scheme:dark]" />
              </div>
              <div>
                <p className="text-white/50 text-xs mb-1.5">To</p>
                <input type="date" data-testid="kiosk-leave-end" value={req.end_date} min={req.start_date || format(new Date(), "yyyy-MM-dd")}
                  onChange={(e) => setReq({ ...req, end_date: e.target.value })}
                  className="w-full h-14 rounded-xl glass px-4 text-white [color-scheme:dark]" />
              </div>
            </div>
            <input type="text" data-testid="kiosk-leave-reason" placeholder="Reason (optional)" value={req.reason}
              onChange={(e) => setReq({ ...req, reason: e.target.value })}
              className="w-full h-14 rounded-xl glass px-4 text-white placeholder:text-[#79728C]" />
            <button onClick={submitLeave} disabled={busy} data-testid="kiosk-leave-submit"
              className="w-full h-16 rounded-2xl bg-[#9D5CFF] hover:bg-[#B380FF] font-heading font-semibold text-xl transition-colors disabled:opacity-50 shadow-[0_0_24px_rgba(157,92,255,0.3)]">
              {busy ? "Submitting…" : "Submit request"}
            </button>
          </div>
        )}

        {step === "revenue" && revForm && (
          <div className="space-y-5" data-testid="kiosk-revenue-form">
            <button onClick={() => setStep("menu")} data-testid="kiosk-revenue-back" className="flex items-center gap-2 text-[#BDB8CC] hover:text-white transition-colors">
              <ArrowLeft size={20} /> Back
            </button>
            <div>
              <p className="text-white/50 text-xs mb-1.5">Date</p>
              <input type="date" data-testid="kiosk-revenue-date" value={revForm.date} max={format(new Date(), "yyyy-MM-dd")}
                onChange={(e) => loadRevenueDate(e.target.value)}
                className="w-full h-14 rounded-xl glass px-4 text-white [color-scheme:dark]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["food_sales", "Food sales (POS)"], ["beverage_sales", "Beverage sales (POS)"],
                ["revenue_additional", "Revenue — additional"],
                ["tips_pos_cash", "Tips POS — cash"], ["tips_pos_card", "Tips POS — card"],
                ["tips_add_cash", "Extra tips — cash"], ["tips_add_card", "Extra tips — card"],
                ["covers_lunch", "Covers — lunch (optional)"], ["covers_dinner", "Covers — dinner (optional)"],
              ].map(([k, label]) => (
                <div key={k}>
                  <p className="text-white/50 text-xs mb-1.5">{label}</p>
                  <input type="number" inputMode="decimal" min="0" step="any" data-testid={`kiosk-revenue-${k}`}
                    value={revForm[k] ?? (k.startsWith("covers") ? "" : 0)}
                    onChange={(e) => setRevForm({ ...revForm, [k]: e.target.value })}
                    className="w-full h-14 rounded-xl glass px-4 text-white text-lg" />
                </div>
              ))}
            </div>
            <div className="flex justify-between text-white/60 text-sm px-1">
              <span>Revenue: <strong className="text-white">R {(Number(revForm.food_sales || 0) + Number(revForm.beverage_sales || 0) + Number(revForm.revenue_additional || 0)).toFixed(2)}</strong></span>
              <span>Tips: <strong className="text-white" data-testid="kiosk-revenue-tips-total">R {(Number(revForm.tips_pos_cash || 0) + Number(revForm.tips_pos_card || 0) + Number(revForm.tips_add_cash || 0) + Number(revForm.tips_add_card || 0)).toFixed(2)}</strong></span>
            </div>
            {Number(revForm.covers_lunch || 0) + Number(revForm.covers_dinner || 0) > 0 && (
              <div className="glass rounded-xl px-4 py-3 space-y-1" data-testid="kiosk-spend-per-head">
                <div className="flex justify-between text-white/60 text-sm">
                  <span>Total covers</span>
                  <strong className="text-white" data-testid="kiosk-total-covers">{Number(revForm.covers_lunch || 0) + Number(revForm.covers_dinner || 0)}</strong>
                </div>
                <div className="flex justify-between text-white/60 text-sm">
                  <span>Food / head</span>
                  <strong className="text-white" data-testid="kiosk-spend-food">R {(Number(revForm.food_sales || 0) / (Number(revForm.covers_lunch || 0) + Number(revForm.covers_dinner || 0))).toFixed(2)}</strong>
                </div>
                <div className="flex justify-between text-white/60 text-sm">
                  <span>Beverage / head</span>
                  <strong className="text-white" data-testid="kiosk-spend-bev">R {(Number(revForm.beverage_sales || 0) / (Number(revForm.covers_lunch || 0) + Number(revForm.covers_dinner || 0))).toFixed(2)}</strong>
                </div>
                <div className="flex justify-between text-white/60 text-sm">
                  <span>Total / head</span>
                  <strong className="text-[#C9A8FF]" data-testid="kiosk-spend-total">R {((Number(revForm.food_sales || 0) + Number(revForm.beverage_sales || 0) + Number(revForm.revenue_additional || 0)) / (Number(revForm.covers_lunch || 0) + Number(revForm.covers_dinner || 0))).toFixed(2)}</strong>
                </div>
                <p className="text-white/40 text-[11px]">Per-head figures exclude tips</p>
              </div>
            )}
            <button onClick={submitRevenue} disabled={busy} data-testid="kiosk-revenue-submit"
              className="w-full h-16 rounded-2xl bg-[#9D5CFF] hover:bg-[#B380FF] font-heading font-semibold text-xl transition-colors disabled:opacity-50 shadow-[0_0_24px_rgba(157,92,255,0.3)]">
              {busy ? "Saving…" : "Save day's figures"}
            </button>
          </div>
        )}

        {step === "invoice" && (
          <div className="space-y-5" data-testid="kiosk-invoice-form">
            <button onClick={() => setStep("menu")} data-testid="kiosk-invoice-back" className="flex items-center gap-2 text-[#BDB8CC] hover:text-white transition-colors">
              <ArrowLeft size={20} /> Back
            </button>
            {!invJobs ? (
              <>
                <label className="block border-2 border-dashed border-[#9D5CFF]/25 rounded-2xl p-8 text-center cursor-pointer hover:border-[#9D5CFF]/50 transition-colors glass">
                  <Camera size={40} weight="bold" className="mx-auto text-white/40 mb-2" />
                  <p className="text-[#79728C] text-sm">Photograph or upload supplier invoices — JPG, PNG or PDF (max 10 files)</p>
                  <input type="file" multiple accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" data-testid="kiosk-invoice-file"
                    onChange={(e) => {
                      const fs = Array.from(e.target.files || []).slice(0, 10);
                      setInvFiles((p) => [...p, ...fs].slice(0, 10));
                      setInvMode(invFiles.length + fs.length > 1 ? "batch" : "single");
                      e.target.value = "";
                    }} />
                </label>
                {invFiles.length > 0 && (
                  <div className="space-y-2" data-testid="kiosk-invoice-list">
                    {invFiles.map((f, i) => (
                      <div key={`${f.name}-${f.size}-${f.lastModified}`} className="flex items-center justify-between gap-2 text-sm glass rounded-xl px-3 py-2" data-testid={`kiosk-invoice-file-${i}`}>
                        <span className="truncate">{f.name}</span>
                        <button onClick={() => setInvFiles(invFiles.filter((_, j) => j !== i))} data-testid={`kiosk-invoice-remove-${i}`} className="text-white/40 hover:text-white"><X size={16} /></button>
                      </div>
                    ))}
                  </div>
                )}
                {invFiles.length > 1 && (
                  <div className="flex gap-2" data-testid="kiosk-invoice-mode">
                    <button onClick={() => setInvMode("batch")} data-testid="kiosk-invoice-mode-batch"
                      className={`flex-1 text-sm px-3 py-3 rounded-xl border transition-colors ${invMode === "batch" ? "border-[#FF5C00] bg-[#FF5C00]/15 text-white font-medium" : "border-white/10 text-[#79728C]"}`}>
                      Separate invoices
                    </button>
                    <button onClick={() => setInvMode("single")} data-testid="kiosk-invoice-mode-single"
                      className={`flex-1 text-sm px-3 py-3 rounded-xl border transition-colors ${invMode === "single" ? "border-[#FF5C00] bg-[#FF5C00]/15 text-white font-medium" : "border-white/10 text-[#79728C]"}`}>
                      Pages of one invoice
                    </button>
                  </div>
                )}
                <button onClick={submitInvoice} disabled={!invFiles.length || busy} data-testid="kiosk-invoice-submit"
                  className="w-full h-16 rounded-2xl bg-[#9D5CFF] hover:bg-[#B380FF] font-heading font-semibold text-xl transition-colors disabled:opacity-50 shadow-[0_0_24px_rgba(157,92,255,0.3)]">
                  {busy ? "Uploading…" : "Extract with AI"}
                </button>
              </>
            ) : (
              <div className="space-y-3" data-testid="kiosk-invoice-results">
                <p className="font-heading font-semibold text-xl">Extraction results</p>
                {invJobs.map((t, i) => {
                  const label = t.status === "processing" ? "Reading…"
                    : t.status === "done" ? (t.auto_accepted
                      ? `Auto-posted${t.summary ? ` — ${t.summary.supplier_name} R${t.summary.amount}` : ""}`
                      : "Sent to review queue")
                    : t.status === "duplicate" ? "Duplicate — already captured" : `Failed${t.error ? `: ${t.error}` : ""}`;
                  const cls = t.status === "processing" ? "text-white/50"
                    : t.status === "done" && t.auto_accepted ? "text-[#9D5CFF]" : "text-[#D4B3FF]";
                  return (
                    <div key={t.jobId} className="glass rounded-xl px-4 py-3" data-testid={`kiosk-invoice-job-${i}`}>
                      <p className="text-sm truncate">{t.name}</p>
                      <p className={`text-sm font-medium mt-0.5 ${cls}`}>{label}</p>
                    </div>
                  );
                })}
                {!invJobs.some((t) => t.status === "processing") && (
                  <div className="space-y-2 pt-2">
                    {invJobs.some((t) => t.status === "done" && !t.auto_accepted) && (
                      <p className="text-white/50 text-sm text-center">Items in the review queue are confirmed on the desktop Stock page.</p>
                    )}
                    <button onClick={() => { setInvFiles([]); setInvJobs(null); setInvMode("single"); }} data-testid="kiosk-invoice-again"
                      className="w-full h-14 rounded-2xl glass glass-hover font-heading font-semibold text-lg transition-colors">
                      Capture more
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <p className="text-center text-[#79728C] text-xs">
          <a href="/login" className="underline" data-testid="kiosk-login-link">Manager sign in</a>
        </p>
      </div>
    </div>
  );
}
